import * as React from 'react';
import { Printer, Tag } from 'lucide-react';
import type { ItemEscopo } from '@/hooks/inventario';
import { compararNatural } from '@/lib/utils';
import { useAbrirNoEdge } from '@/hooks/useAbrirNoEdge';
import { CodigoBarras } from '@/componentes/etiqueta/CodigoBarras';
import { Botao } from '@/componentes/ui/botao';

/**
 * Os papéis do inventário: a folha de contagem e as etiquetas de endereço.
 *
 * FOLHA DE CONTAGEM — CEGA POR CONSTRUÇÃO
 * Endereço, produto e uma caixa em branco para a quantidade. O saldo não
 * entra no papel (nem chega a este componente): com o número impresso ao
 * lado, a contagem vira confirmação. Endereço vazio na foto também sai, com a
 * linha "vazio? confirme" — é o que o conferente precisa olhar.
 *
 * ETIQUETA DE ENDEREÇO — O MESMO CÓDIGO QUE O COLETOR JÁ LÊ
 * O coletor valida o endereço pelo número do LOCALIZADOR
 * (Usp_Validar_Localizador_Inventario) e as etiquetas que estão nas ruas
 * hoje carregam esse número com 13 dígitos (aparecem como `0000000055928` no
 * log do coletor). Code 128 em SVG, como as etiquetas do recebimento.
 */

export interface ItemFolha {
  localizador: number;
  endereco: string | null;
  produto: number | null;
  descricao: string | null;
  ean: string | null;
}

/** Tira o saldo antes de chegar ao papel. */
export function paraFolha(itens: readonly ItemEscopo[]): ItemFolha[] {
  return itens.map(({ localizador, endereco, produto, descricao, ean }) => ({
    localizador,
    endereco,
    produto,
    descricao,
    ean,
  }));
}

export const codigoDoEndereco = (localizador: number): string => String(localizador).padStart(13, '0');

export const CSS_FOLHA_CONTAGEM = `
@page { size: A4 portrait; margin: 8mm 7mm 12mm 7mm; }
.folha-inv { font-family: "Segoe UI", ui-sans-serif, system-ui, sans-serif; color: #14161a; background: #fff; font-size: 8.6pt; line-height: 1.3; }
.folha-inv * { box-sizing: border-box; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.folha-inv .topo { display: flex; border: 0.3mm solid #14161a; }
.folha-inv .topo-id { flex: 1; padding: 2.4mm 3mm; }
.folha-inv .marca { font-size: 11.5pt; font-weight: 800; }
.folha-inv .sub { font-size: 9pt; font-weight: 600; margin-top: 0.6mm; }
.folha-inv .topo-doc { background: #14161a; color: #fff; padding: 2.4mm 3mm; min-width: 40mm; text-align: right; }
.folha-inv .doc-rot { font-size: 6.6pt; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.75; }
.folha-inv .doc-num { font-size: 19pt; font-weight: 800; line-height: 1; }
.folha-inv .dados { display: flex; border: 0.3mm solid #14161a; border-top: none; }
.folha-inv .campo { flex: 1; padding: 1.6mm 3mm; border-right: 0.2mm solid #c7cbd1; }
.folha-inv .campo:last-child { border-right: none; }
.folha-inv .rot { display: block; font-size: 6.4pt; letter-spacing: 0.09em; text-transform: uppercase; color: #6b7280; }
.folha-inv .val { font-size: 9pt; font-weight: 600; min-height: 4.5mm; }
.folha-inv table { width: 100%; border-collapse: collapse; margin-top: 2.5mm; }
.folha-inv thead { display: table-header-group; }
.folha-inv th { background: #14161a; color: #fff; font-size: 6.8pt; text-transform: uppercase; letter-spacing: 0.07em; text-align: left; padding: 1.5mm; }
.folha-inv td { padding: 1.4mm 1.5mm; vertical-align: middle; border-bottom: 0.2mm solid #d6d9de; }
.folha-inv tr { break-inside: avoid; page-break-inside: avoid; }
.folha-inv tr.novo-end td { border-top: 0.6mm solid #14161a; }
.folha-inv .end { font-family: ui-monospace, Consolas, monospace; font-weight: 700; font-size: 9.5pt; white-space: nowrap; }
.folha-inv .loc { font-size: 6.6pt; color: #6b7280; }
.folha-inv .sku { font-family: ui-monospace, Consolas, monospace; white-space: nowrap; }
.folha-inv .desc { font-weight: 600; }
.folha-inv .vazio { font-style: italic; color: #6b7280; }
.folha-inv .caixa { width: 22mm; height: 7mm; border: 0.35mm solid #14161a; }
.folha-inv .rodape { margin-top: 5mm; display: flex; gap: 8mm; }
.folha-inv .assin { flex: 1; border-top: 0.3mm solid #14161a; padding-top: 1mm; font-size: 7pt; color: #6b7280; }
`;

export const CSS_ETIQUETAS_ENDERECO = `
@page { size: A4 portrait; margin: 8mm 6mm; }
.etq-end { font-family: "Segoe UI", ui-sans-serif, system-ui, sans-serif; color: #000; background: #fff; }
.etq-end * { box-sizing: border-box; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.etq-end .grade { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3mm; }
.etq-end .etq { height: 34mm; border: 0.3mm dashed #9ca3af; padding: 2mm 3mm; display: flex; flex-direction: column; align-items: center; justify-content: center; break-inside: avoid; page-break-inside: avoid; }
.etq-end .nome { font-family: ui-monospace, Consolas, monospace; font-size: 15pt; font-weight: 800; letter-spacing: 0.02em; }
.etq-end .num { font-family: ui-monospace, Consolas, monospace; font-size: 7.5pt; margin-top: 0.8mm; letter-spacing: 0.08em; }
.etq-end svg { margin-top: 1.5mm; }
`;

export function FolhaContagem({
  inventario,
  escopo,
  centro,
  contagem,
  itens,
}: {
  inventario: string;
  escopo: number;
  centro: number;
  contagem: string;
  itens: readonly ItemFolha[];
}): JSX.Element {
  const ordenados = [...itens].sort(
    (a, b) =>
      compararNatural(a.endereco ?? String(a.localizador), b.endereco ?? String(b.localizador)) ||
      compararNatural(a.descricao ?? '', b.descricao ?? ''),
  );
  const enderecos = new Set(itens.map((i) => i.localizador)).size;
  let anterior: number | null = null;
  return (
    <div className="folha-inv">
      <div className="topo">
        <div className="topo-id">
          <div className="marca">Folha de contagem · inventário</div>
          <div className="sub">{inventario}</div>
        </div>
        <div className="topo-doc">
          <div className="doc-rot">Escopo</div>
          <div className="doc-num">{escopo}</div>
        </div>
      </div>
      <div className="dados">
        <div className="campo">
          <span className="rot">Centro</span>
          <div className="val">{centro}</div>
        </div>
        <div className="campo">
          <span className="rot">Contagem</span>
          <div className="val">{contagem}</div>
        </div>
        <div className="campo">
          <span className="rot">Endereços</span>
          <div className="val">{enderecos}</div>
        </div>
        <div className="campo" style={{ flex: 2 }}>
          <span className="rot">Contador</span>
          <div className="val" />
        </div>
        <div className="campo">
          <span className="rot">Data</span>
          <div className="val" />
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{ width: '24mm' }}>Endereço</th>
            <th style={{ width: '18mm' }}>SKU</th>
            <th>Produto</th>
            <th style={{ width: '30mm' }}>EAN</th>
            <th style={{ width: '25mm' }}>Qtd. contada</th>
          </tr>
        </thead>
        <tbody>
          {ordenados.map((i) => {
            const novo = i.localizador !== anterior;
            anterior = i.localizador;
            return (
              <tr key={`${i.localizador}-${i.produto ?? 'v'}`} className={novo ? 'novo-end' : undefined}>
                <td>
                  {novo ? (
                    <>
                      <div className="end">{i.endereco ?? i.localizador}</div>
                      <div className="loc">LOC {i.localizador}</div>
                    </>
                  ) : null}
                </td>
                <td className="sku">{i.produto ?? ''}</td>
                <td>
                  {i.produto === null ? (
                    <span className="vazio">vazio na foto — confirme se está vazio mesmo</span>
                  ) : (
                    <span className="desc">{i.descricao}</span>
                  )}
                </td>
                <td className="sku">{i.ean ?? ''}</td>
                <td>
                  <div className="caixa" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="rodape">
        <div className="assin">Contador</div>
        <div className="assin">Conferente</div>
        <div className="assin">Digitado por / data</div>
      </div>
    </div>
  );
}

export function EtiquetasEndereco({
  enderecos,
}: {
  enderecos: readonly { localizador: number; endereco: string | null }[];
}): JSX.Element {
  return (
    <div className="etq-end">
      <div className="grade">
        {enderecos.map((e) => (
          <div key={e.localizador} className="etq">
            <div className="nome">{e.endereco ?? e.localizador}</div>
            <CodigoBarras texto={codigoDoEndereco(e.localizador)} larguraPt={160} alturaPt={34} />
            <div className="num">{codigoDoEndereco(e.localizador)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Os dois botões de impressão do escopo. Os papéis ficam montados fora da
 * tela — é deles que a impressão copia (ver imprimirPapel).
 */
export function ImprimirEscopo({
  inventario,
  escopo,
  centro,
  contagem,
  itens,
}: {
  inventario: string;
  escopo: number;
  centro: number;
  contagem: string;
  itens: readonly ItemEscopo[];
}): JSX.Element {
  const folha = React.useRef<HTMLDivElement>(null);
  const etiquetas = React.useRef<HTMLDivElement>(null);
  // O A4 sai pelo Edge, com prévia e escolha de impressora (ver abrirPapelNoEdge).
  const edge = useAbrirNoEdge();
  const semSaldo = React.useMemo(() => paraFolha(itens), [itens]);
  const enderecos = React.useMemo(() => {
    const m = new Map<number, string | null>();
    for (const i of itens) if (!m.has(i.localizador)) m.set(i.localizador, i.endereco);
    return [...m.entries()]
      .map(([localizador, endereco]) => ({ localizador, endereco }))
      .sort((a, b) => compararNatural(a.endereco ?? '', b.endereco ?? ''));
  }, [itens]);

  return (
    <>
      <Botao
        variante="fantasma"
        tamanho="micro"
        disabled={itens.length === 0}
        title="Folha cega: endereço, produto e caixa para a quantidade, sem saldo"
        onClick={() =>
          folha.current &&
          void edge.abrir({
            modo: 'papel',
            conteudo: folha.current,
            titulo: `Folha de contagem escopo ${escopo}`,
            css: CSS_FOLHA_CONTAGEM,
          })
        }
        carregando={edge.abrindo}
      >
        <Printer className="h-3.5 w-3.5" />
        Folha
      </Botao>
      <Botao
        variante="fantasma"
        tamanho="micro"
        disabled={enderecos.length === 0}
        title="Etiquetas com o código de barras do endereço (o mesmo que o coletor lê)"
        onClick={() =>
          etiquetas.current &&
          void edge.abrir({
            modo: 'papel',
            conteudo: etiquetas.current,
            titulo: `Etiquetas de endereco escopo ${escopo}`,
            css: CSS_ETIQUETAS_ENDERECO,
          })
        }
        carregando={edge.abrindo}
      >
        <Tag className="h-3.5 w-3.5" />
        Etiquetas
      </Botao>
      {edge.erro ? <span className="text-[11.5px] text-status-divergencia">{edge.erro}</span> : null}
      <div className="pointer-events-none h-0 overflow-hidden" aria-hidden>
        <style>{`${CSS_FOLHA_CONTAGEM.replace(/@page[^}]*}/, '')}${CSS_ETIQUETAS_ENDERECO.replace(/@page[^}]*}/, '')}`}</style>
        <div ref={folha} className="absolute left-[-10000px] top-0 w-[210mm]">
          <FolhaContagem inventario={inventario} escopo={escopo} centro={centro} contagem={contagem} itens={semSaldo} />
        </div>
        <div ref={etiquetas} className="absolute left-[-10000px] top-0 w-[210mm]">
          <EtiquetasEndereco enderecos={enderecos} />
        </div>
      </div>
    </>
  );
}
